"use client";

import {
  Bot,
  GraduationCap,
  Gift,
  Code2,
  ArrowLeft,
  type LucideIcon,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { AppView } from "@/stores/useAppStore";
import { pushView } from "@/stores/useAppStore";

type Service = {
  icon: LucideIcon;
  title: string;
  desc: string;
  view: AppView;
  cta: string;
};

const SERVICES: Service[] = [
  {
    icon: Bot,
    title: "اکانت هوش مصنوعی",
    desc: "دسترسی به اکانت‌های رسمی ChatGPT Plus، Claude Pro، Midjourney و سایر سرویس‌های هوش مصنوعی.",
    view: "ai-accounts",
    cta: "مشاهده",
  },
  {
    icon: GraduationCap,
    title: "آکادمی آنلاین",
    desc: "دوره‌های تعاملی با استاندارد SCORM؛ پیشرفت یادگیری شما به‌صورت خودکار ذخیره می‌شود.",
    view: "academy",
    cta: "ورود به آکادمی",
  },
  {
    icon: Gift,
    title: "سیستم کمیسیون",
    desc: "با معرفی دوستانتان به پلتفرم، از هر خرید زیرمجموعه درصدی به‌عنوان کمیسیون دریافت کنید.",
    view: "commission",
    cta: "کسب درآمد",
  },
  {
    icon: Code2,
    title: "سفارش پروژه",
    desc: "طراحی سایت و اپلیکیشن اختصاصی توسط تیم برنامه‌نویسی ما؛ درخواست خود را ثبت کنید.",
    view: "order-site",
    cta: "ثبت درخواست",
  },
];

export function Features() {
  return (
    <section
      id="services"
      aria-labelledby="services-heading"
      className="py-20 md:py-24"
    >
      <div className="mx-auto w-full max-w-7xl px-4">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
            خدمات ما
          </span>
          <h2
            id="services-heading"
            className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl"
          >
            همه چیز برای یادگیری و درآمد هوش مصنوعی
          </h2>
          <p className="mt-3 text-muted-foreground">
            از آموزش تخصصی تا اکانت رسمی و فرصت درآمدزایی — یک پلتفرم، چهار
            خدمت کاربردی.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s) => (
            <article
              key={s.title}
              className={cn(
                "group flex flex-col gap-4 rounded-2xl border border-border bg-card p-6",
                "hover:border-primary/50 hover:shadow-lg transition",
              )}
            >
              <span
                aria-hidden
                className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/20 transition-colors group-hover:bg-primary group-hover:text-primary-foreground"
              >
                <s.icon className="size-6" />
              </span>
              <div className="space-y-1.5">
                <h3 className="text-lg font-semibold">{s.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {s.desc}
                </p>
              </div>
              <Button
                variant="outline"
                className="mt-auto w-full gap-2 group-hover:border-primary/50 group-hover:text-primary"
                onClick={() => pushView(s.view)}
              >
                {s.cta}
                <ArrowLeft className="size-4" />
              </Button>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Features;
